<script setup lang="ts">
import { computed, ref } from "vue";
import { Input } from "@/components/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

interface Props {
    modelValue: string;
    disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
});

const emit = defineEmits<{
    (e: "update:modelValue", value: string): void;
}>();

const isOpen = ref(false);

const presetColors = [
    "#ef4444", // red
    "#f97316", // orange
    "#eab308", // yellow
    "#22c55e", // green
    "#10b981", // emerald
    "#14b8a6", // teal
    "#0ea5e9", // sky
    "#3b82f6", // blue
    "#6366f1", // indigo
    "#8b5cf6", // violet
    "#a855f7", // purple
    "#ec4899", // pink
    "#1f2937", // gray-800
    "#ffffff", // white
];

const localValue = computed({
    get: () => props.modelValue,
    set: (val: string) => emit("update:modelValue", val),
});

function selectPreset(color: string) {
    localValue.value = color;
    isOpen.value = false;
}

function isValidHex(color: string): boolean {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

const displayColor = computed(() => {
    return isValidHex(props.modelValue) ? props.modelValue : "#000000";
});
</script>

<template>
    <Popover v-model:open="isOpen">
        <PopoverTrigger as-child :disabled="disabled">
            <div
                class="relative flex cursor-pointer"
                :class="{ 'cursor-not-allowed opacity-50': disabled }"
            >
                <div
                    class="left-3 h-5 w-5 rounded border-iron-300 shadow-sm absolute top-1/2 -translate-y-1/2 border"
                    :style="{ backgroundColor: displayColor }"
                />
                <Input
                    v-model="localValue"
                    class="pl-10 uppercase"
                    :disabled="disabled"
                    @click.stop
                />
            </div>
        </PopoverTrigger>
        <PopoverContent class="p-3 w-auto" align="start">
            <div class="gap-1.5 grid grid-cols-7">
                <button
                    v-for="color in presetColors"
                    :key="color"
                    type="button"
                    class="h-6 w-6 rounded border-iron-200 shadow-sm focus:ring-z-primary-500 border transition-transform hover:scale-110 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                    :style="{ backgroundColor: color }"
                    :class="{
                        'ring-z-primary-500 ring-2 ring-offset-1':
                            modelValue.toLowerCase() === color.toLowerCase(),
                    }"
                    @click="selectPreset(color)"
                />
            </div>
        </PopoverContent>
    </Popover>
</template>
